import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import AddStaffRosterDialog from "../../../components/admin/AddStaffRosterDialog";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { buildApiUrl } from "../../../config/api";
import Popup from "../../../components/Popup";

const DEFAULT_STATUSES = ["Present", "Absent", "Late", "Leave", "Half-day", "Other"];

function todayYMD() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function nowHHmm() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function emptyRow(staffMember) {
    return {
        teacherId: staffMember.id,
        status: staffMember.attendance?.status || "Present",
        arrivalTime: staffMember.attendance?.arrivalTime || "",
        departureTime: staffMember.attendance?.departureTime || "",
        notes: staffMember.attendance?.notes || "",
    };
}

function rowHasAttendanceData(row) {
    if (!row) return false;
    return Boolean(
        row.arrivalTime ||
            row.departureTime ||
            (row.status && row.status !== "Present") ||
            String(row.notes || "").trim(),
    );
}

export default function StaffAttendanceRegister() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser } = useSelector((state) => state.user);
    const schoolId = currentUser?._id || currentUser?.id;

    const [sessionDate, setSessionDate] = useState(todayYMD());
    const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
    const [staff, setStaff] = useState([]);
    const [rows, setRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingRowId, setSavingRowId] = useState("");
    const [error, setError] = useState("");
    const [popupMessage, setPopupMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState("success");
    const [showPopup, setShowPopup] = useState(false);
    const [addStaffOpen, setAddStaffOpen] = useState(false);

    const loadRegister = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(
                buildApiUrl(`/StaffAttendanceRegister/${schoolId}`),
                { params: { date: sessionDate } },
            );
            if (data?.message && !Array.isArray(data?.staff)) {
                setError(data.message);
                setStaff([]);
                setRows({});
                return;
            }
            const list = Array.isArray(data?.staff) ? data.staff : [];
            setStaff(list);
            setStatuses(Array.isArray(data?.statuses) ? data.statuses : DEFAULT_STATUSES);
            const nextRows = {};
            list.forEach((member) => {
                nextRows[member.id] = emptyRow(member);
            });
            setRows(nextRows);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not load staff register");
            setStaff([]);
            setRows({});
        } finally {
            setLoading(false);
        }
    }, [schoolId, sessionDate]);

    useEffect(() => {
        loadRegister();
    }, [loadRegister]);

    useEffect(() => {
        if (searchParams.get("add") === "1") {
            setAddStaffOpen(true);
            const next = new URLSearchParams(searchParams);
            next.delete("add");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const isToday = sessionDate === todayYMD();

    const savedTodayCount = useMemo(
        () => staff.filter((member) => member.attendance != null).length,
        [staff],
    );

    const formatDisplayDate = (ymd) => {
        const [y, m, d] = ymd.split("-").map(Number);
        if (!y || !m || !d) return ymd;
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };
    const recordedCount = useMemo(
        () =>
            staff.filter((member) => {
                const row = rows[member.id];
                return row && (row.arrivalTime || row.departureTime || row.status !== "Present");
            }).length,
        [staff, rows],
    );

    const updateRow = (teacherId, patch) => {
        setRows((prev) => ({
            ...prev,
            [teacherId]: { ...prev[teacherId], ...patch },
        }));
    };

    const saveRecords = useCallback(
        async (records, { quiet = false, rowId = "" } = {}) => {
            if (!schoolId) return false;
            const toSave = records.filter(rowHasAttendanceData);
            if (!toSave.length) {
                if (!quiet) {
                    setError("Record an arrival, departure, status, or note before saving.");
                }
                return false;
            }

            if (rowId) setSavingRowId(rowId);
            else setSaving(true);
            setError("");

            try {
                const { data } = await axios.post(
                    buildApiUrl(`/StaffAttendanceRegister/${schoolId}`),
                    {
                        date: sessionDate,
                        records: toSave,
                        recordedById: schoolId,
                    },
                );
                if (data?.message && data?.savedCount == null) {
                    setError(data.message);
                    return false;
                }
                if (!quiet) {
                    setPopupSeverity("success");
                    setPopupMessage(
                        data?.savedCount === 1
                            ? "Attendance saved."
                            : `Saved attendance for ${data.savedCount} staff member(s).`,
                    );
                    setShowPopup(true);
                }
                await loadRegister();
                return true;
            } catch (e) {
                setError(e.response?.data?.message || e.message || "Could not save staff attendance");
                return false;
            } finally {
                if (rowId) setSavingRowId("");
                else setSaving(false);
            }
        },
        [schoolId, sessionDate, loadRegister],
    );

    const markArrivalNow = async (teacherId) => {
        const nextRow = {
            ...rows[teacherId],
            teacherId,
            arrivalTime: nowHHmm(),
            status: "Present",
        };
        updateRow(teacherId, { arrivalTime: nextRow.arrivalTime, status: "Present" });
        await saveRecords([nextRow], { quiet: true, rowId: teacherId });
    };

    const markDepartureNow = async (teacherId) => {
        const nextRow = {
            ...rows[teacherId],
            teacherId,
            departureTime: nowHHmm(),
        };
        updateRow(teacherId, { departureTime: nextRow.departureTime });
        await saveRecords([nextRow], { quiet: true, rowId: teacherId });
    };

    const handleSaveRow = async (teacherId) => {
        const row = rows[teacherId];
        if (!row) return;
        await saveRecords([row], { rowId: teacherId });
    };

    const handleSaveAll = async () => {
        const records = staff.map((member) => rows[member.id]).filter(Boolean);
        await saveRecords(records);
    };

    if (!schoolId) {
        return (
            <Container sx={{ py: 4 }}>
                <Alert severity="info">Sign in as school admin to use the staff sign-in book.</Alert>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.secondary.main, 0.05)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Popup
                message={popupMessage}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
            <AddStaffRosterDialog
                open={addStaffOpen}
                onClose={() => setAddStaffOpen(false)}
                onStaffAdded={loadRegister}
                schoolId={schoolId}
            />
            <Container maxWidth="xl">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ md: "center" }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.12),
                                    color: "secondary.main",
                                }}
                            >
                                <AccessTimeOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography variant="overline" color="text.secondary">
                                    School office
                                </Typography>
                                <Typography variant="h5" fontWeight={800}>
                                    Staff sign-in book
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Daily register — each day keeps its own record. Record arrival in the morning;
                                    add departure later the same day. Use the date picker to view past days.
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            <TextField
                                type="date"
                                size="small"
                                label="Register date"
                                value={sessionDate}
                                onChange={(e) => setSessionDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            {!isToday ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSessionDate(todayYMD())}
                                >
                                    Today
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                startIcon={<PersonAddAlt1OutlinedIcon />}
                                onClick={() => setAddStaffOpen(true)}
                                disabled={loading || saving}
                            >
                                Add names
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshOutlinedIcon />}
                                onClick={loadRegister}
                                disabled={loading || saving}
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SaveOutlinedIcon />}
                                onClick={handleSaveAll}
                                disabled={loading || saving || recordedCount === 0}
                            >
                                {saving ? "Saving…" : "Save recorded rows"}
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {!isToday ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        Viewing sign-in records for <strong>{formatDisplayDate(sessionDate)}</strong>. Switch to{" "}
                        <strong>Today</strong> to record the current day.
                    </Alert>
                ) : (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                        Today&apos;s register — {formatDisplayDate(sessionDate)}. A new blank record starts each
                        calendar day; yesterday&apos;s times stay on that date.
                    </Alert>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <Chip
                        icon={<Groups2OutlinedIcon />}
                        label={`${staff.length} staff on register`}
                    />
                    <Chip
                        color={isToday ? "secondary" : "default"}
                        variant="outlined"
                        label={isToday ? "Today's register" : formatDisplayDate(sessionDate)}
                    />
                    <Chip
                        color="secondary"
                        variant="outlined"
                        label={`${savedTodayCount} saved for this day`}
                    />
                    <Chip
                        variant="outlined"
                        label={`${recordedCount} pending in form`}
                    />
                </Stack>

                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Staff member</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Arrival</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Departure</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Notes</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    Quick record
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            Loading staff register…
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : staff.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Stack spacing={2} sx={{ my: 2 }}>
                                            <Alert severity="info">
                                                No staff on the sign-in book yet. Add someone to start recording
                                                arrival and departure.
                                            </Alert>
                                            <Button
                                                variant="contained"
                                                startIcon={<PersonAddAlt1OutlinedIcon />}
                                                onClick={() => setAddStaffOpen(true)}
                                                sx={{ alignSelf: "flex-start" }}
                                            >
                                                Add names
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                staff.map((member) => {
                                    const row = rows[member.id] || emptyRow(member);
                                    return (
                                        <TableRow key={member.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {member.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {member.email || member.phone || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={member.roleLabel || member.role || "Staff"}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControl size="small" fullWidth>
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        label="Status"
                                                        value={row.status}
                                                        onChange={(e) =>
                                                            updateRow(member.id, { status: e.target.value })
                                                        }
                                                    >
                                                        {statuses.map((status) => (
                                                            <MenuItem key={status} value={status}>
                                                                {status}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="time"
                                                    value={row.arrivalTime}
                                                    onChange={(e) =>
                                                        updateRow(member.id, { arrivalTime: e.target.value })
                                                    }
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="time"
                                                    value={row.departureTime}
                                                    onChange={(e) =>
                                                        updateRow(member.id, { departureTime: e.target.value })
                                                    }
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    placeholder="Optional note"
                                                    value={row.notes}
                                                    onChange={(e) =>
                                                        updateRow(member.id, { notes: e.target.value })
                                                    }
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Record arrival now (saves immediately)">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => markArrivalNow(member.id)}
                                                            disabled={savingRowId === member.id}
                                                        >
                                                            <LoginOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Record departure now (saves immediately)">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => markDepartureNow(member.id)}
                                                            disabled={savingRowId === member.id}
                                                        >
                                                            <LogoutOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Save this row">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleSaveRow(member.id)}
                                                                disabled={
                                                                    savingRowId === member.id ||
                                                                    !rowHasAttendanceData(row)
                                                                }
                                                            >
                                                                <SaveOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </Box>
    );
}
